import RAPIER from '@dimforge/rapier3d-compat';

import { skyLids, type Box, type LevelGeometry } from '../core/level.ts';
import type { Barrier } from '../core/placed.ts';

/**
 * The one place a level becomes colliders, for both ends of the wire.
 *
 * The client's `GameWorld` and the server's `PhysicsWorld` each own a Rapier
 * world, and for a long while each built its level from its own loop. They
 * drifted three times, and every time it looked like something else:
 *
 *   - Flying bodies kept autostep on the server only: reported as choppy ghost
 *     movement, not as a disagreement.
 *   - The server skipped ceilings entirely, so a Mentalist or a ghost flew out
 *     of the top of the map on the server while the client predicted a bump.
 *   - Solid spell walls existed only on the client, so a Wall of Ice stopped
 *     you on your own screen and the server walked you through it.
 *
 * So the decision is made once, here: which boxes collide, in which groups, at
 * what rotation, plus the invisible lids over any floor open to the sky, plus
 * the solid spell barriers. Both worlds hold one of these and ask it to build;
 * neither creates a level collider of its own. `test/worldParity.test.ts`
 * builds both worlds from the same geometry and compares what they contain.
 */

/**
 * Collision groups.
 *
 * Line-of-sight queries must see level geometry and nothing else. When they saw
 * the player's own capsule too, every projectile died on the tick it was born
 * (it spawns at eye height, inside that capsule) and every dummy decided its
 * shot at the player was blocked — by the player.
 *
 * Forced unsigned with `>>> 0`, which is not fussiness. `0xffff << 16` is
 * *negative* in JavaScript and Rapier reads a group as unsigned; handed the
 * negative on the server, every raycast returned nothing — no floor, no walls,
 * no line of sight. The client carried the signed form for a long time and got
 * away with it, which is exactly why there is now only one copy.
 */
export const GROUP_LEVEL = 0x0001;
export const GROUP_CHARACTER = 0x0002;

/** Level geometry, lids and solid barriers: belong to LEVEL, collide with everything. */
export const LEVEL_GROUPS = ((GROUP_LEVEL << 16) | 0xffff) >>> 0;
/**
 * Characters: belong to CHARACTER, and Rapier lets them meet only the LEVEL.
 *
 * Mages do block one another, but not through Rapier: `CharacterBodies` clips
 * each move against the other mages itself, in plain arithmetic, before Rapier
 * sweeps what is left against the level. See that file for why.
 *
 * Rapier's character controller does not read a collider's own groups when it
 * sweeps — it honours only the groups it is *handed* — so this is passed to
 * `computeColliderMovement` too (`MOVE_FILTER`).
 */
export const CHARACTER_GROUPS = ((GROUP_CHARACTER << 16) | GROUP_LEVEL) >>> 0;
/** Sight and projectile rays: belong to everything, hit only LEVEL. */
export const RAY_GROUPS = ((0xffff << 16) | GROUP_LEVEL) >>> 0;

/** What Rapier sweeps a moving body against: the level, and nothing else. */
export const MOVE_FILTER = CHARACTER_GROUPS;

/** A turn about the vertical as a quaternion, which is what Rapier wants. */
export function yawQuat(yaw: number): { x: number; y: number; z: number; w: number } {
  return { x: 0, y: Math.sin(yaw / 2), z: 0, w: Math.cos(yaw / 2) };
}

/** A solid box in the world, as Rapier will be told about it. */
function cuboid(physics: RAPIER.World, b: Pick<Box, 'pos' | 'size' | 'yaw'>): RAPIER.Collider {
  return physics.createCollider(
    RAPIER.ColliderDesc.cuboid(b.size.x / 2, b.size.y / 2, b.size.z / 2)
      .setTranslation(b.pos.x, b.pos.y, b.pos.z)
      // Line of sight is a Rapier raycast against these, so an angled wall stops
      // spells along its true face the moment the collider is turned.
      .setRotation(yawQuat(b.yaw ?? 0))
      .setCollisionGroups(LEVEL_GROUPS),
  );
}

/** What a collider looks like from outside, for comparing two worlds. */
export interface ColliderRecord {
  pos: { x: number; y: number; z: number };
  half: { x: number; y: number; z: number };
  yaw: number;
  groups: number;
}

export class LevelColliders {
  private readonly physics: RAPIER.World;
  private level: RAPIER.Collider[] = [];
  private lids: Box[] = [];
  private readonly barriers = new Map<number, RAPIER.Collider>();

  constructor(physics: RAPIER.World) {
    this.physics = physics;
  }

  /**
   * Replaces whatever level was there with this one.
   *
   * Every box collides, ceilings included. Spell barriers go with the map they
   * were cast on: a Wall of Ice standing in mid-air on the next map is very
   * funny once and then ruins a round.
   */
  build(geometry: LevelGeometry): void {
    this.clear();
    for (const b of geometry.boxes) this.level.push(cuboid(this.physics, b));

    /*
     * The lids: invisible, and nowhere but here.
     *
     * Not in `geometry.boxes`, because the renderer draws every box it is given
     * and a lid is not something anybody should see — the moonlight still has
     * to come through a skylight. Not in the generated arena either, because
     * that module is rebuilt from the blockout and anything written into it by
     * hand is lost. Derived from the geometry instead, so a new skylight in
     * Godot is sealed the moment it is exported.
     */
    this.lids = skyLids(geometry);
    for (const lid of this.lids) this.level.push(cuboid(this.physics, lid));

    this.refresh();
  }

  /**
   * Mirrors the simulation's solid barriers into Rapier.
   *
   * Core owns the barrier list outright and decides everything about it — what
   * a wall stops, who it slows, when it ends. This exists only so that a solid
   * one is something you physically cannot walk through, which is a collision
   * question and therefore Rapier's.
   *
   * Barriers that pass bodies get no collider at all — an Airwall slows you via
   * `moveScale`, which core computes, and a collider would make it solid.
   *
   * Cheap to call every tick: a barrier that is already mirrored costs a map
   * lookup, and the query structures are only rebuilt when something changed.
   */
  syncBarriers(barriers: readonly Barrier[]): void {
    let changed = false;
    const live = new Set<number>();

    for (const b of barriers) {
      if (!b.blocksBodies) continue;
      live.add(b.id);
      if (this.barriers.has(b.id)) continue;
      this.barriers.set(b.id, cuboid(this.physics, b));
      changed = true;
    }

    for (const [id, collider] of this.barriers) {
      if (live.has(id)) continue;
      this.physics.removeCollider(collider, false);
      this.barriers.delete(id);
      changed = true;
    }

    if (changed) this.refresh();
  }

  /** Every level collider, lid and barrier removed. */
  clear(): void {
    for (const c of this.level) this.physics.removeCollider(c, false);
    this.level = [];
    this.lids = [];
    for (const c of this.barriers.values()) this.physics.removeCollider(c, false);
    this.barriers.clear();
  }

  /** The lids this level was given. For tests and the F3 overlay. */
  get skyLids(): readonly Box[] {
    return this.lids;
  }

  /** How many solid barriers are mirrored right now. */
  get barrierCount(): number {
    return this.barriers.size;
  }

  /**
   * Everything this builder put into the world, in a form two worlds can be
   * compared by. Sorted, so the order things were added in does not matter.
   */
  describe(): ColliderRecord[] {
    const out: ColliderRecord[] = [];
    for (const c of [...this.level, ...this.barriers.values()]) {
      const t = c.translation();
      const r = c.rotation();
      const shape = c.shape as RAPIER.Cuboid;
      const round = (n: number) => Math.round(n * 1000) / 1000;
      out.push({
        pos: { x: round(t.x), y: round(t.y), z: round(t.z) },
        half: {
          x: round(shape.halfExtents.x), y: round(shape.halfExtents.y), z: round(shape.halfExtents.z),
        },
        yaw: round(2 * Math.atan2(r.y, r.w)),
        groups: c.collisionGroups(),
      });
    }
    const key = (r: ColliderRecord) => `${r.pos.x},${r.pos.y},${r.pos.z},${r.half.x},${r.half.y},${r.half.z}`;
    return out.sort((a, b) => key(a).localeCompare(key(b)));
  }

  /**
   * Brings the query structures up to date.
   *
   * Rapier answers raycasts and character sweeps from structures it rebuilds on
   * `step()`. A wall cast mid-tick would otherwise be invisible to every sweep
   * until the world next stepped — on the server, until the next batch of
   * ticks had already walked somebody through it.
   */
  private refresh(): void {
    this.physics.updateSceneQueries();
  }
}
